/**
 * useSearchData Hook
 * Fetches all BRR sessions from the session store and provides
 * search-specific filtering for the cross-user search page.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { SessionAuditClient, SessionStatus } from '../services';
import { BRRSession } from '../services/types';
import { StatusFilter } from './useDashboardData';

export type QuickFilter = 'all' | 'my-brrs' | 'in-progress' | 'pending-qp-review' | 'completed';

export interface SearchFilters {
  searchText: string;
  batchNumber: string;
  product: string;
  site: string;
  country: string;
  dateFrom: string;
  dateTo: string;
  reviewer: string;
  status: StatusFilter;
  quickFilter: QuickFilter;
}

const DEFAULT_FILTERS: SearchFilters = {
  searchText: '',
  batchNumber: '',
  product: '',
  site: '',
  country: '',
  dateFrom: '',
  dateTo: '',
  reviewer: '',
  status: 'all',
  quickFilter: 'my-brrs',
};

const IN_PROGRESS_STATUSES = [SessionStatus.Draft, SessionStatus.InProgress];

const STATUS_FILTER_MAP: Record<StatusFilter, number | number[] | null> = {
  all: null,
  'in-progress': IN_PROGRESS_STATUSES,
  delayed: IN_PROGRESS_STATUSES,
  submitted: SessionStatus.Submitted,
  completed: SessionStatus.Completed,
  rejected: SessionStatus.Rejected,
};

interface UseSearchDataOptions {
  sessionClient: SessionAuditClient;
  userEmail: string;
}

interface UseSearchDataReturn {
  sessions: BRRSession[];
  filteredSessions: BRRSession[];
  filters: SearchFilters;
  setFilters: (partial: Partial<SearchFilters>) => void;
  clearFilters: () => void;
  loading: boolean;
  error: Error | null;
  isTruncated: boolean;
  refresh: () => void;
  distinctProducts: string[];
  distinctReviewers: string[];
  distinctSites: string[];
  distinctCountries: string[];
}

function parseDetails(session: BRRSession, detailsColumn: string): Record<string, any> | null {
  try {
    const raw = session[detailsColumn];
    if (raw) return JSON.parse(raw);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to parse session details:', e);
    }
  }
  return null;
}

/**
 * Check if a batch ID is valid (not a temporary placeholder).
 * TEMP- prefixed IDs are created before the user selects a real batch
 * and should not be displayed in search results.
 */
function isValidBatchId(batchId: string | undefined): boolean {
  return !!batchId && !batchId.startsWith('TEMP-');
}

export const useSearchData = ({
  sessionClient,
  userEmail,
}: UseSearchDataOptions): UseSearchDataReturn => {
  const [sessions, setSessions] = useState<BRRSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [filters, setFiltersState] = useState<SearchFilters>(DEFAULT_FILTERS);

  const columns = sessionClient.columns;

  // Filter out TEMP placeholder sessions (created before user selects a real batch)
  // This must happen before any other processing so counts, distinct values, etc. exclude them
  const validSessions = useMemo(() => {
    return sessions.filter((s) => isValidBatchId(s[columns.batchid]));
  }, [sessions, columns]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { sessions: fetched, isTruncated: truncated } = await sessionClient.getAllSessions();
      setSessions(fetched);
      setIsTruncated(truncated);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [sessionClient]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Extract distinct values for filter dropdowns (from valid sessions only)
  const distinctProducts = useMemo(() => {
    const values = new Set<string>();
    for (const s of validSessions) {
      const v = s[columns.productname];
      if (v) values.add(v);
    }
    return [...values].sort();
  }, [validSessions, columns]);

  const distinctReviewers = useMemo(() => {
    const values = new Set<string>();
    for (const s of validSessions) {
      const v = s[columns.useremail];
      if (v) values.add(v);
    }
    return [...values].sort();
  }, [validSessions, columns]);

  const distinctSites = useMemo(() => {
    const values = new Set<string>();
    for (const s of validSessions) {
      const details = parseDetails(s, columns.details);
      const site = details?.cmoName || details?.site;
      if (site) values.add(site);
    }
    return [...values].sort();
  }, [validSessions, columns]);

  const distinctCountries = useMemo(() => {
    const values = new Set<string>();
    for (const s of validSessions) {
      const details = parseDetails(s, columns.details);
      const country = details?.country || details?.destinationCountry;
      if (country) values.add(country);
    }
    return [...values].sort();
  }, [validSessions, columns]);

  const filteredSessions = useMemo(() => {
    let result = validSessions;

    // Quick filter
    if (filters.quickFilter === 'my-brrs') {
      const lower = userEmail.toLowerCase();
      result = result.filter((s) => (s[columns.useremail] || '').toLowerCase() === lower);
    } else if (filters.quickFilter === 'in-progress') {
      result = result.filter((s) => s[columns.status] === SessionStatus.InProgress);
    } else if (filters.quickFilter === 'pending-qp-review') {
      result = result.filter((s) => s[columns.status] === SessionStatus.Submitted);
    } else if (filters.quickFilter === 'completed') {
      result = result.filter((s) => s[columns.status] === SessionStatus.Completed);
    }

    // Status filter (advanced)
    const statusCode = STATUS_FILTER_MAP[filters.status];
    if (statusCode !== null) {
      const codes = Array.isArray(statusCode) ? statusCode : [statusCode];
      result = result.filter((s) => codes.includes(s[columns.status]));
    }

    // Batch number
    if (filters.batchNumber.trim()) {
      const term = filters.batchNumber.toLowerCase().trim();
      result = result.filter((s) => (s[columns.batchid] || '').toLowerCase().includes(term));
    }

    // Product
    if (filters.product) {
      result = result.filter((s) => (s[columns.productname] || '') === filters.product);
    }

    // Reviewer
    if (filters.reviewer.trim()) {
      const term = filters.reviewer.toLowerCase().trim();
      result = result.filter((s) => (s[columns.useremail] || '').toLowerCase().includes(term));
    }

    // Site (from details JSON)
    if (filters.site) {
      result = result.filter((s) => {
        const details = parseDetails(s, columns.details);
        const site = details?.cmoName || details?.site;
        return site === filters.site;
      });
    }

    // Country (from details JSON)
    if (filters.country) {
      result = result.filter((s) => {
        const details = parseDetails(s, columns.details);
        const country = details?.country || details?.destinationCountry;
        return country === filters.country;
      });
    }

    // Date range
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom).getTime();
      result = result.filter((s) => {
        const started = s[columns.startedat];
        if (!started) return false;
        return new Date(started).getTime() >= from;
      });
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      const toMs = to.getTime();
      result = result.filter((s) => {
        const started = s[columns.startedat];
        if (!started) return false;
        return new Date(started).getTime() <= toMs;
      });
    }

    // Free-text search across multiple fields
    if (filters.searchText.trim()) {
      const term = filters.searchText.toLowerCase().trim();
      result = result.filter((s) => {
        const batchId = (s[columns.batchid] || '').toLowerCase();
        const product = (s[columns.productname] || '').toLowerCase();
        const email = (s[columns.useremail] || '').toLowerCase();
        if (batchId.includes(term) || product.includes(term) || email.includes(term)) return true;
        const details = parseDetails(s, columns.details);
        if (details) {
          const lotId = String(details.inspectionLotId || '').toLowerCase();
          if (lotId.includes(term)) return true;
          const materialId = String(details.materialId || '').toLowerCase();
          if (materialId.includes(term)) return true;
          const vendorBatchId = String(details.vendorBatchId || '').toLowerCase();
          if (vendorBatchId.includes(term)) return true;
        }
        return false;
      });
    }

    return result;
  }, [validSessions, filters, columns, userEmail]);

  const setFilters = useCallback((partial: Partial<SearchFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...partial }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  return {
    sessions: validSessions,
    filteredSessions,
    filters,
    setFilters,
    clearFilters,
    loading,
    error,
    isTruncated,
    refresh: fetchSessions,
    distinctProducts,
    distinctReviewers,
    distinctSites,
    distinctCountries,
  };
};
